import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authApi, onUnauthorized } from "../api/client.js";

/* Who is signed in, and what this deployment allows.
 *
 * The session itself is an httpOnly cookie the browser cannot read, so
 * "am I signed in?" is a question only the server can answer: the app asks
 * once on load and trusts the answer until a request comes back 401. */

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [methods, setMethods] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reachable, setReachable] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { user: me } = await authApi.me();
      setUser(me);
      setReachable(true);
      return me;
    } catch (e) {
      if (e.code === "offline") setReachable(false);
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      /* Both answers are needed before the first paint: who you are, and
         which sign-in methods to offer if you are nobody. */
      const [, cfg] = await Promise.all([
        refresh(),
        authApi.config().catch(() => ({ google: false, allowedDomains: [] })),
      ]);
      if (!live) return;
      setMethods(cfg);
      setLoading(false);
    })();
    return () => {
      live = false;
    };
  }, [refresh]);

  /* Any 401 from anywhere in the app drops the session here, which flips
     every guarded route back to the sign-in page in one move. */
  useEffect(() => onUnauthorized(() => setUser(null)), []);

  const value = useMemo(
    () => ({
      user,
      methods,
      loading,
      reachable,
      isAdmin: user?.role === "admin",
      refresh,
      async signInWithGoogle(credential) {
        const { user: me } = await authApi.google(credential);
        setUser(me);
        return me;
      },
      async signOut() {
        try {
          await authApi.logout();
        } finally {
          setUser(null);
        }
      },
      setUser,
    }),
    [user, methods, loading, reachable, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
